<template>
  <v-select :model-value="modelValue" :items="options" item-title="name" item-value="id" multiple
    density="compact" variant="outlined" hide-details :label="flat ? undefined : label"
    :placeholder="flat ? label : undefined" class="tag-filter-select" :class="{ 'is-flat': flat }"
    @update:model-value="$emit('update:modelValue', $event)">
    <template #selection="{ item, index }">
      <TagChip v-if="index < maxChips" :name="item.raw.name" :color="item.raw.color" :icon="item.raw.icon"
        size="sm" class="sel-chip">
        <template #trailing>
          <button type="button" class="chip-x" aria-label="Remove tag"
            @mousedown.stop.prevent @click.stop="removeTag(item.value)">
            <v-icon size="12">mdi-close</v-icon>
          </button>
        </template>
      </TagChip>
      <span v-else-if="index === maxChips" class="more-count">+{{ modelValue.length - maxChips }}</span>
    </template>
    <template #item="{ item, props: itemProps }">
      <v-list-item v-bind="itemProps" :title="undefined">
        <template #title>
          <TagChip :name="item.raw.name" :color="item.raw.color" :icon="item.raw.icon" size="sm" />
        </template>
      </v-list-item>
    </template>
  </v-select>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';
import TagChip from '@/components/tags/TagChip.vue';

const props = defineProps({
  // Array of selected tag ids.
  modelValue: { type: Array, default: () => [] },
  // Tag objects to choose from (id/name/color/icon) — the caller passes only the tags actually in use.
  options: { type: Array, default: () => [] },
  label: { type: String, default: 'Tags' },
  // How many chips to show before collapsing the rest into a "+N" counter.
  maxChips: { type: Number, default: 3 },
  // Flat pill styling (dark fill, hairline border, placeholder instead of floating label) so it can
  // sit alongside native <select> pill filters. The caller adds the matching field colours via :deep.
  flat: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);

// Remove one tag from the selection without reopening the menu (the chip's X).
function removeTag(id) {
  emit('update:modelValue', props.modelValue.filter(v => v !== id));
}
</script>

<style scoped>
.tag-filter-select :deep(.v-field) { border-radius: 10px; background: rgba(255, 255, 255, 0.03); }
.tag-filter-select :deep(.sel-chip) { margin: 2px 4px 2px 0; }
.more-count { font-size: 0.78rem; color: #8aa0aa; font-weight: 600; align-self: center; }

/* Inline remove (X) on each selected chip — removes it without reopening the menu. */
.chip-x {
  display: inline-flex; align-items: center; justify-content: center;
  margin-left: 3px; margin-right: -2px; padding: 0; border: none; background: transparent;
  color: currentColor; opacity: 0.65; cursor: pointer; border-radius: 50%; line-height: 0;
}
.chip-x:hover { opacity: 1; }

/* Flat pill variant — matches native .pill-select filters (dark fill, hairline border, teal focus). */
.tag-filter-select.is-flat :deep(.v-field) {
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  min-height: 38px;
  font-size: 0.85rem;
  color: var(--duallo-text-secondary, #b0bec5);
  transition: border-color 0.2s ease;
}
/* Match the native .pill-select text exactly: app font, 0.85rem, normal tracking — Vuetify defaults to
   Roboto 16px with extra letter-spacing on the inner input, so set it on the input/placeholder itself. */
.tag-filter-select.is-flat :deep(.v-field__input),
.tag-filter-select.is-flat :deep(.v-field__input input),
.tag-filter-select.is-flat :deep(.v-field__input input::placeholder),
.tag-filter-select.is-flat :deep(.v-select__selection),
.tag-filter-select.is-flat :deep(.v-select__selection-text) {
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  font-weight: 400;
  letter-spacing: normal;
}
.tag-filter-select.is-flat :deep(.v-field__outline) { display: none; }
.tag-filter-select.is-flat :deep(.v-field:hover),
.tag-filter-select.is-flat :deep(.v-field--focused) { border-color: rgba(102, 199, 180, 0.5); }
.tag-filter-select.is-flat :deep(.v-field__input) {
  min-height: 36px; padding-top: 0; padding-bottom: 0; padding-inline: 12px 2px; align-items: center;
}
.tag-filter-select.is-flat :deep(.v-field__input input::placeholder) { color: #8aa0aa; opacity: 1; }
/* Pull the dropdown chevron in close to the text, like the native pill's chevron. */
.tag-filter-select.is-flat :deep(.v-field__append-inner) { padding-inline-start: 2px; }
.tag-filter-select.is-flat :deep(.v-field__append-inner .v-icon) { font-size: 20px; opacity: 0.8; }
</style>
